import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { setNavBlock, confirmLeaveIfBlocked } from "@/lib/navGuard";
import { EXP_CATS, DOC_CATS, ExpenseFields, BankFields, RibFields, DocFields } from "@/components/SmartScanFields";

const TYPES = [
  ["facture", "Receipt"],
  ["releve_bancaire", "Landmark"],
  ["rib", "CreditCard"],
  ["document", "FolderOpen"],
];

export const SmartScan = ({ back }) => {
  const { t } = useApp();
  const { isRole } = useAuth();
  const isTres = isRole("tresorier");
  const navigate = useNavigate();
  const fileRef = useRef();
  const camRef = useRef();
  const [phase, setPhase] = useState("idle");   // idle | analyzing | review | saving | done
  const [pieces, setPieces] = useState([]);     // pages/photos d'un MÊME document, ajoutées AVANT l'analyse
  const [res, setRes] = useState(null);
  const [type, setType] = useState("facture");
  const [exp, setExp] = useState({});
  const [docF, setDocF] = useState({});
  const [bank, setBank] = useState({});
  const [rib, setRib] = useState({});
  const [saved, setSaved] = useState(null);
  const [progress, setProgress] = useState(0);          // % réel d'upload des photos
  const [analyzeStage, setAnalyzeStage] = useState("upload");  // upload (réel) | ai (analyse serveur)

  const isImage = (f) => (f.type || "").startsWith("image/");
  const revokeAll = (list) => list.forEach((p) => p.url && URL.revokeObjectURL(p.url));
  const reset = () => { revokeAll(pieces); setPieces([]); setPhase("idle"); setRes(null); setSaved(null); setProgress(0); setAnalyzeStage("upload"); };

  // Verrou anti-perte : bloque le changement de page + le rafraîchissement pendant l'analyse IA.
  const analyzing = phase === "analyzing";
  useEffect(() => {
    setNavBlock(analyzing, t("ss_leave_confirm"));
    const onBeforeUnload = (e) => { if (analyzing) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => { window.removeEventListener("beforeunload", onBeforeUnload); setNavBlock(false); };
  }, [analyzing, t]);
  const guardedBack = () => { if (confirmLeaveIfBlocked()) back(); };

  // Ajoute des pages/photos (une par une ou plusieurs d'un coup). Un PDF/CSV/Excel doit rester seul.
  const addFiles = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    const added = list.map((f) => ({ file: f, url: isImage(f) ? URL.createObjectURL(f) : null }));
    const next = [...pieces, ...added];
    if (next.length > 1 && next.some((p) => !isImage(p.file))) {
      revokeAll(added);
      toast.error(t("ss_single_pdf"));
      return;
    }
    setPieces(next);
  };

  const removePiece = (i) => setPieces((prev) => {
    if (prev[i]?.url) URL.revokeObjectURL(prev[i].url);
    return prev.filter((_, idx) => idx !== i);
  });

  // L'IA n'est lancée QU'APRÈS confirmation que toutes les pages sont ajoutées.
  const analyze = async () => {
    if (!pieces.length) return;
    setProgress(0);
    setAnalyzeStage("upload");
    setPhase("analyzing");
    const fd = new FormData();
    pieces.forEach((p) => fd.append("files", p.file));
    try {
      const { data } = await api.post("/smart-scan/analyze", fd, {
        timeout: 150000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setProgress(pct);
          if (pct >= 100) setAnalyzeStage("ai");   // envoi terminé → lecture IA côté serveur
        },
      });
      setProgress(100);
      setRes(data);
      setType(data.doc_type);
      const ex = data.expense || {};
      setExp({
        supplier: ex.supplier || "", amount: ex.total_amount ?? "", vat_amount: ex.vat_amount ?? "",
        date: ex.date || new Date().toISOString().slice(0, 10),
        category: EXP_CATS.includes(ex.category) ? ex.category : "autres",
        payment_status: "en_attente", payment_method: "bank",
      });
      const d = data.document || {};
      setDocF({
        category: DOC_CATS.includes(d.category) ? d.category : "autre",
        title: d.title || "", description: d.description || "", visibility: "public",
        supplier: d.supplier || "", service_type: d.service_type || "",
        start_date: d.start_date || "", end_date: d.end_date || "", amount: d.amount ?? "",
      });
      const b = data.bank || {};
      const txs = b.transactions || [];
      const lastTxDate = txs.map((x) => String(x.date || "")).sort().slice(-1)[0] || "";
      setBank({ statement_date: b.statement_date || lastTxDate, closing_balance: b.closing_balance ?? "", transactions: txs });
      const rb = data.rib || {};
      setRib({
        kind: rb.is_residence === false ? "externe" : "residence",
        label: "", holder: rb.holder || "", bank_name: rb.bank_name || "",
        agency: rb.agency || "", rib_number: rb.rib_number || "", iban: rb.iban || "", swift: rb.swift || "",
      });
      revokeAll(pieces);
      setPieces([]);
      setPhase("review");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ss_analyze_failed"));
      setPhase("idle");   // les pièces sont conservées pour corriger et réessayer
    }
  };

  const confirm = async () => {
    if (type === "facture" && !(parseFloat(exp.amount) > 0)) { toast.error(t("ss_amount_required")); return; }
    if (type === "document" && !docF.title.trim()) { toast.error(t("ss_title_required")); return; }
    if (type === "rib" && !(rib.rib_number || "").trim()) { toast.error(t("ss_rib_required")); return; }
    if (type === "releve_bancaire" && (!bank.statement_date || bank.closing_balance === "" || bank.closing_balance === null)) {
      toast.error(t("ss_bank_required")); return;
    }
    if (type === "facture" && exp.payment_status === "paye" && exp.payment_method === "cash" && !isTres) {
      toast.error(t("cash_only_tresorier")); return;
    }
    if (type === "facture" && exp.payment_method === "avance") {
      if (!exp.advanced_by_id) { toast.error(t("advance_select_required")); return; }
      if (!exp._advConfirmed) { toast.error(t("advance_confirm_required")); return; }
    }
    setPhase("saving");
    try {
      const { data } = await api.post("/smart-scan/confirm", {
        doc_type: type,
        file_url: res.file_url, file_path: res.file_path,
        file_name: res.file_name, content_type: res.content_type,
        expense: { ...exp, amount: parseFloat(exp.amount || 0), vat_amount: exp.vat_amount ? parseFloat(exp.vat_amount) : null },
        bank: { ...bank, closing_balance: bank.closing_balance !== "" && bank.closing_balance !== null ? parseFloat(bank.closing_balance) : null },
        rib,
        document: { ...docF, amount: docF.amount ? parseFloat(docF.amount) : null, pages: res.pages || null },
      });
      setSaved(data);
      setPhase("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
      setPhase("review");
    }
  };

  if (phase === "done" && saved) {
    return (
      <div className="space-y-5 text-center pt-10" data-testid="smart-done">
        <div className="h-20 w-20 rounded-full bg-brand/10 text-brand dark:text-primary flex items-center justify-center mx-auto animate-rise">
          <Icons.CheckCircle2 className="h-11 w-11" />
        </div>
        <h2 className="text-2xl font-bold">{t("ss_saved_" + saved.doc_type)}</h2>
        {saved.doc_type === "releve_bancaire" && (
          <p className="text-muted-foreground text-sm" data-testid="smart-recon-summary">
            ✅ {saved.matched} {t("ss_matched")} · ⚠️ {saved.anomalies} {t("ss_anomalies")}
          </p>
        )}
        <button onClick={() => navigate(saved.target)} data-testid="smart-view-btn"
          className="w-full h-14 rounded-full bg-gradient-to-br from-majorelle to-[#243b8f] text-white font-bold text-lg shadow-ios active:scale-[0.98] transition-transform">
          {t("ss_view")}
        </button>
        <button onClick={reset} data-testid="smart-scan-more"
          className="w-full h-12 rounded-full border border-input font-semibold">
          {t("ss_scan_more")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="smart-scan">
      <button onClick={guardedBack} className="flex items-center gap-2 font-bold text-lg mb-2" data-testid="smart-back">
        <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" /> {t("ss_title")}
      </button>

      {(phase === "idle" || phase === "analyzing") && (
        <>
          <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.csv,.xlsx,.xls"
            className="hidden" onChange={addFiles} data-testid="smart-file-input" />
          <input ref={camRef} type="file" accept="image/*" capture="environment"
            className="hidden" onChange={addFiles} data-testid="smart-cam-input" />

          {phase === "analyzing" ? (
            <div className="w-full rounded-3xl border-2 border-dashed border-majorelle/40 bg-majorelle/5 p-6 flex flex-col items-center justify-center gap-4" data-testid="smart-analyzing">
              <Icons.Sparkles className="h-8 w-8 text-majorelle dark:text-primary animate-pulse" />
              <span className="font-semibold text-majorelle dark:text-primary text-center">
                {analyzeStage === "upload" ? `${t("ss_uploading")} ${progress}%` : t("ss_analyzing")}
              </span>
              <div className="w-full h-3 rounded-full bg-majorelle/15 overflow-hidden" data-testid="smart-progress">
                {analyzeStage === "upload" ? (
                  <div className="h-full bg-majorelle rounded-full transition-[width] duration-200"
                    style={{ width: `${progress}%` }} data-testid="smart-progress-bar" />
                ) : (
                  <div className="h-full w-1/3 bg-majorelle rounded-full ss-indeterminate" data-testid="smart-progress-ai" />
                )}
              </div>
              <span className="text-xs text-muted-foreground text-center">{pieces.length} {t("ss_pages_added")}</span>
              <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 text-center flex items-center gap-1.5">
                <Icons.TriangleAlert className="h-4 w-4 shrink-0" /> {t("ss_dont_leave")}
              </span>
            </div>
          ) : pieces.length === 0 ? (
            <>
              <button onClick={() => fileRef.current?.click()} data-testid="smart-upload-btn"
                className="w-full h-44 rounded-3xl border-2 border-dashed border-majorelle/40 bg-majorelle/5 flex flex-col items-center justify-center gap-3 text-majorelle dark:text-primary">
                <Icons.Sparkles className="h-10 w-10" />
                <span className="font-bold">{t("ss_drop")}</span>
                <span className="text-xs text-muted-foreground px-8 text-center">{t("ss_hint")}</span>
              </button>
              <button onClick={() => camRef.current?.click()} data-testid="smart-camera-btn"
                className="w-full h-12 rounded-full border border-input font-semibold flex items-center justify-center gap-2">
                <Icons.Camera className="h-4 w-4" /> {t("take_photo_cam")}
              </button>
            </>
          ) : (
            <div className="space-y-4" data-testid="smart-collect">
              <div className="rounded-3xl glass shadow-ios p-4 space-y-3">
                <p className="font-bold text-sm" data-testid="smart-pieces-count">
                  {pieces.length} {t("ss_pages_added")}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {pieces.map((p, i) => (
                    <div key={p.url || `${p.file.name}-${i}`} className="relative rounded-2xl overflow-hidden border border-border bg-secondary aspect-[3/4]" data-testid={`smart-piece-${i}`}>
                      {p.url ? (
                        <img src={p.url} alt={`page ${i + 1}`} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex flex-col items-center justify-center gap-1 text-muted-foreground p-2">
                          <Icons.FileText className="h-7 w-7" />
                          <span className="text-[10px] text-center break-all line-clamp-2">{p.file.name}</span>
                        </div>
                      )}
                      <span className="absolute bottom-1 start-1 text-[10px] font-bold bg-black/60 text-white rounded-full px-1.5 py-0.5">{i + 1}</span>
                      <button onClick={() => removePiece(i)} data-testid={`smart-piece-remove-${i}`}
                        className="absolute top-1 end-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                        <Icons.X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 p-4" data-testid="smart-pages-question">
                <p className="font-bold text-amber-800 dark:text-amber-300">{t("ss_pages_q")}</p>
                <p className="text-xs text-amber-700/80 dark:text-amber-300/70 mt-1">{t("ss_pages_hint")}</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => fileRef.current?.click()} data-testid="smart-add-page"
                  className="h-12 rounded-xl border border-input bg-background font-semibold text-sm flex items-center justify-center gap-1.5">
                  <Icons.Plus className="h-4 w-4" /> {t("ss_add_page")}
                </button>
                <button onClick={() => camRef.current?.click()} data-testid="smart-add-camera"
                  className="h-12 rounded-xl border border-input bg-background font-semibold text-sm flex items-center justify-center gap-1.5">
                  <Icons.Camera className="h-4 w-4" /> {t("take_photo_cam")}
                </button>
              </div>

              <button onClick={analyze} data-testid="smart-analyze-btn"
                className="w-full h-14 rounded-full bg-gradient-to-br from-majorelle to-[#243b8f] text-white font-bold text-lg flex items-center justify-center gap-2 shadow-ios active:scale-[0.98] transition-transform">
                <Icons.Sparkles className="h-5 w-5" /> {t("ss_all_added")}
              </button>
            </div>
          )}
        </>
      )}

      {(phase === "review" || phase === "saving") && res && (
        <>
          <div className="rounded-3xl glass shadow-ios p-4 space-y-1" data-testid="smart-summary-card">
            <p className="text-xs font-bold uppercase tracking-wide text-majorelle dark:text-primary flex items-center gap-1.5">
              <Icons.Sparkles className="h-3.5 w-3.5" /> {t("ss_detected")}
            </p>
            <p className="font-semibold" data-testid="smart-summary">{res.summary}</p>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Icons.CornerDownRight className="h-4 w-4 shrink-0" /> {t("ss_dest_" + type)}
            </p>
          </div>

          <div>
            <p className="text-sm font-semibold text-muted-foreground mb-1">{t("ss_wrong_type")}</p>
            <div className="grid grid-cols-4 gap-2">
              {TYPES.map(([val, ic]) => {
                const Ico = Icons[ic];
                const active = type === val;
                return (
                  <button key={val} onClick={() => setType(val)} data-testid={`smart-type-${val}`}
                    className={`h-16 rounded-2xl border font-semibold text-xs flex flex-col items-center justify-center gap-1 px-1 text-center transition-colors ${active ? "bg-majorelle text-white border-majorelle" : "bg-background border-input"}`}>
                    <Ico className="h-4 w-4" /> {t("ss_type_" + val)}
                  </button>
                );
              })}
            </div>
          </div>

          {type === "facture" && <ExpenseFields t={t} exp={exp} setExp={setExp} isTres={isTres} />}

          {type === "releve_bancaire" && <BankFields t={t} bank={bank} setBank={setBank} />}

          {type === "rib" && <RibFields t={t} rib={rib} setRib={setRib} />}

          {type === "document" && <DocFields t={t} docF={docF} setDocF={setDocF} />}

          <button onClick={confirm} disabled={phase === "saving"} data-testid="smart-confirm-btn"
            className="w-full h-14 rounded-full bg-gradient-to-br from-majorelle to-[#243b8f] text-white font-bold text-lg flex items-center justify-center gap-2 shadow-ios disabled:opacity-60 active:scale-[0.98] transition-transform">
            {phase === "saving" ? t("loading") : (<><Icons.Check className="h-5 w-5" /> {t("ss_confirm")}</>)}
          </button>
        </>
      )}
    </div>
  );
};
